import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import { isServer, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import type { Request, Response } from 'express';

export const BR_COOKIE_NAME_IDENTITY = '_br_uid_2';

export const generateBrUID2Cookie = (): string =>
    `uid=${Math.round(Math.random() * 10e12)}:v=app:ts=0:hc=1`;

export const getServerBloomreachCookie = (
    req: Partial<Request>,
    res: Partial<Response>,
): string => {
    // This is only for server side
    if (!isServer) {
        return '';
    }
    let brUID2 = '';
    let cookies: Record<string, string> = {};
    if (req.cookies) {
        cookies = req.cookies as Record<string, string>;
        brUID2 = cookies[BR_COOKIE_NAME_IDENTITY];
    }
    if (!brUID2) {
        brUID2 = generateBrUID2Cookie();
        if (res.cookie && typeof res.cookie === 'function') {
            cookies[BR_COOKIE_NAME_IDENTITY] = brUID2;
            res.cookie(BR_COOKIE_NAME_IDENTITY, brUID2);
        }
    }
    return brUID2;
};

export const getClientBloomreachCookie = (): string => {
    // This is only for client side
    if (isServer) {
        return '';
    }
    let brUID2 = Cookies.get(BR_COOKIE_NAME_IDENTITY) as string;
    if (!brUID2) {
        brUID2 = generateBrUID2Cookie();
        Cookies.set(BR_COOKIE_NAME_IDENTITY, brUID2);
    }
    return brUID2;
};

/**
 * This hook is used to get the bloomreach cookie
 *
 * clientOnly: the server cookie will not be generated on the server side.
 * blockServerCookie: the server cookie will not be send to the client.
 *
 * @param clientOnly - if true, then the server cookie will not be used
 * @param blockServerCookie - if true, then the server cookie will not be used
 * @returns a function that returns the bloomreach cookie
 */
export const useBloomreachCookieGetter = (
    clientOnly: boolean = true,
    blockServerCookie: boolean = false, // keep the server cookie from being send to client. This will make sure the server generated cookies is not passed to the client.
): (() => string | undefined) => {
    const serverContext = useServerContext();
    /**
     * This is used to pass the server cookie to the client
     * so that the client can use it to make requests to bloomreach
     * blockServerCookie is used to block the server cookie from being send to the client
     * mostly used for category pages where the server cookie would be cached.
     */
    const { data: serverCookie } = useQuery<string>({
        queryKey: ['bloomreach-server-cookie', String(clientOnly)],
        queryFn: () => getServerBloomreachCookie(serverContext?.req, serverContext?.res),
        staleTime: Infinity,
        enabled: isServer && !clientOnly && !blockServerCookie, // only fetch server cookie if not client only and the blockServerCookie is false
    });

    return () => {
        if (isServer) {
            if (blockServerCookie) {
                // if blockServerCookie is true, return client cookie, do not use the server cookie hook.
                return getServerBloomreachCookie(serverContext?.req, serverContext?.res);
            }
            // if client only then the server cookie will be undefined since enabled is false
            // this is on purpose for using from bloomreach recommendation
            return serverCookie;
        }
        // if client only and in the client side, return client cookie
        return getClientBloomreachCookie();
    };
};
